import { FC, memo } from 'react';
import parse from 'html-react-parser';
import {
  Typography as TypographyBase,
  TypographyBaseProps,
  TypographyTypeMap,
  TypographyVariantMap,
  TypographyColorMap,
  TypographyWeightMap,
} from '@tempusfinance/common-ui';

import './Typography.scss';

export type TypographyVariant =
  | 'nav-item'
  | 'body-sm'
  | 'body-md'
  | 'body-lg'
  | 'heading-xs'
  | 'heading-lg'
  | 'heading-md'
  | 'heading-sm'
  | 'button-xs'
  | 'button-sm'
  | 'button-lg'
  | 'input-body-sm'
  | 'input-body-md'
  | 'input-body-lg'
  | 'table-heading'
  | 'table-body-sm'
  | 'table-body-md'
  | 'table-body-lg'
  | 'input-amount'
  | 'input-ticker';
export type TypographyColor =
  | 'text-supporting-darkbg'
  | 'text-supporting'
  | 'text-primary-darkbg'
  | 'text-error'
  | 'text-success'
  | 'text-primary'
  | 'text-warning';
export type TypographyWeight = 'regular' | 'medium' | 'semi-bold' | 'bold';
export type TypographyType = 'regular';

const typographyVariantMap: TypographyVariantMap = {
  'body-sm': {
    fontSize: '12px',
    lineHeight: '14px',
  },
  'body-md': {
    fontSize: '14px',
    lineHeight: '18px',
  },
  'body-lg': {
    fontSize: '16px',
    lineHeight: '20px',
  },
  'heading-xs': {
    fontSize: '16px',
    lineHeight: '18px',
  },
  'heading-sm': {
    fontSize: '18px',
    lineHeight: '18px',
  },
  'heading-md': {
    fontSize: '24px',
    lineHeight: '24px',
  },
  'heading-lg': {
    fontSize: '28px',
    lineHeight: '28px',
  },
  'button-xs': {
    fontSize: '12px',
    lineHeight: '14px',
  },
  'button-sm': {
    fontSize: '14px',
    lineHeight: '16px',
  },
  'button-lg': {
    fontSize: '16px',
    lineHeight: '16px',
  },
  'input-body-sm': {
    fontSize: '12px',
    lineHeight: '16px',
  },
  'table-body-sm': {
    fontSize: '12px',
    lineHeight: '14px',
  },
  'input-body-md': {
    fontSize: '14px',
    lineHeight: '16px',
  },
  'input-body-lg': {
    fontSize: '16px',
    lineHeight: '20px',
  },
  'table-heading': {
    fontSize: '11px',
    lineHeight: 'normal',
    letterSpacing: '0.66px',
    textTransform: 'uppercase',
  },
  'table-body-md': {
    fontSize: '14px',
    lineHeight: '16px',
  },
  'table-body-lg': {
    fontSize: '16px',
    lineHeight: '20px',
  },
  'input-amount': {
    fontSize: '16px',
    lineHeight: '20px',
  },
  'input-ticker': {
    fontSize: '16px',
    lineHeight: '20px',
  },
};

const typographyColorMap: TypographyColorMap = {
  'text-supporting-darkbg': 'var(--text-supporting-darkbg)',
  'text-supporting': 'var(--text-supporting)',
  'text-primary-darkbg': 'var(--text-primary-darkbg)',
  'text-error': 'var(--text-error)',
  'text-success': 'var(--text-success)',
  'text-primary': 'var(--text-primary)',
  'text-warning': 'var(--text-warning)',
};

const typographyWeightMap: TypographyWeightMap = {
  regular: 400,
  medium: 500,
  'semi-bold': 600,
  bold: 700,
};

const typographyTypeMap: TypographyTypeMap = {
  regular: '"Instrument Sans", sans-serif',
};

export type TypographyProps = TypographyBaseProps<
  TypographyVariant,
  TypographyColor,
  TypographyWeight,
  TypographyType
> & {
  html?: string;
};

const TypographyComponent: FC<TypographyProps> = props => {
  const { className, html, children, ...typographyProps } = props;

  let derivedClassName = 'nostra__typography';

  if (className) {
    derivedClassName += ` ${className}`;
  }

  return (
    <TypographyBase<TypographyVariant, TypographyColor, TypographyWeight, TypographyType>
      {...typographyProps}
      className={derivedClassName}
      variantMap={typographyVariantMap}
      colorMap={typographyColorMap}
      weightMap={typographyWeightMap}
      typeMap={typographyTypeMap}
    >
      {html ? parse(html) : children}
    </TypographyBase>
  );
};

export const Typography = memo(TypographyComponent);
